import { GoogleGenAI, Type, HarmCategory, HarmBlockThreshold } from "@google/genai";
import { CreativeBrief, CampaignConcept, VariationStrength } from "../types";

/**
 * HELPER: Initialize fresh AI client for every call.
 */
const getAI = () => {
  let apiKey = typeof process !== 'undefined' ? process.env.API_KEY : "";
  
  if (!apiKey || apiKey === "undefined" || apiKey === "") {
    apiKey = localStorage.getItem('STUDIO_USER_API_KEY') || "";
  }
  
  if (!apiKey || apiKey === "undefined" || apiKey === "") {
    throw new Error("API_KEY_MISSING");
  }
  
  return new GoogleGenAI({ apiKey });
};

/**
 * Robustly extracts JSON from a string that might contain Markdown backticks or conversational text.
 */
const extractJSON = (text: string) => {
  try {
    // Attempt direct parse first
    return JSON.parse(text);
  } catch (e) {
    // Try to extract content between ```json and ```
    const match = text.match(/```json\s?([\s\S]*?)\s?```/) || 
                  text.match(/```\s?([\s\S]*?)\s?```/) ||
                  text.match(/\{[\s\S]*\}/) || // Find anything that looks like an object
                  text.match(/\[[\s\S]*\]/);   // Find anything that looks like an array
                  
    if (match && match[0]) {
      try {
        return JSON.parse(match[0].trim());
      } catch (innerError) {
        throw new Error("Failed to parse extracted JSON.");
      }
    }
    throw new Error("No valid JSON structure found in response.");
  }
};

// SIMPLIFIED PHYSICS: Removed brand names (Sony, Canon, Leica) to avoid "Recitation" (Copyright) blocks
const PHOTOGRAPHY_PHYSICS = {
  'Phase One IQ4': 'Medium format aesthetics, high dynamic range, ultra-fine detail.',
  'Leica M11': 'Professional optical clarity, sharp transitions, street photography style.',
  'Sony A7R V': 'High-resolution digital crispness, accurate sensor clarity, hyper-realistic.',
  '35mm Film': 'Analog film stock aesthetics, organic grain, soft highlights, vintage texture.',
  'Canon EOS R5': 'Professional digital clarity, accurate skin tones, commercial standard.',
  'Hasselblad X2D': 'Studio medium format detail, exceptional micro-contrast, depth.',
  'Fujifilm GFX': 'Rich color science, massive dynamic range, cinematic texture.',
  'Polaroid I-2': 'Instant film texture, chemical color shifts, soft focus edges.',
};

const LIGHTING_PHYSICS = {
  'Rembrandt': 'Dramatic chiaroscuro shadows, distinct facial lighting triangle.',
  'Butterfly': 'High-fashion glamour lighting, soft shadow beneath the nose.',
  'Golden Hour': 'Warm directional sunlight, natural lens flare, soft highlights.',
  'Studio Softbox': 'Broad diffused white light, wrap-around illumination, minimal shadows.',
  'High-Key': 'Bright environment, shadowless, clean commercial style.',
  'Cyberpunk Neon': 'Vivid neon colors, mixed color temperature, cinematic haze.',
  'Moody Cinematic': 'Low-key lighting, atmospheric shadows, dramatic contrast.',
  'Ring Light': 'Frontal beauty illumination, halo reflection, flat shadows.',
  'Rim Light': 'Backlit silhouette, strong edge lighting, contour focus.',
  'Candlelight': 'Warm low-light glow, high ISO noise texture, intimate atmosphere.',
};

/**
 * Enhanced error mapping for user-friendly diagnostics
 */
const mapError = (err: any): string => {
  // Robust extraction of error message from various object shapes
  let msg = typeof err === 'string' ? err : (err.message || "");
  
  if (!msg && err?.error?.message) {
    msg = err.error.message;
  }
  
  // Last resort: stringify the error object if we can't find a message property
  if (!msg) {
    try { msg = JSON.stringify(err); } catch {}
  }

  const upperMsg = msg.toUpperCase();

  // Log detailed error for debugging purposes (browser console)
  console.error("Gemini Service Error:", err);

  // Auth & Permissions
  if (upperMsg.includes("401") || upperMsg.includes("INVALID_ARGUMENT")) return "DIAGNOSTIC_KEY_INVALID";
  if (upperMsg.includes("403") || upperMsg.includes("PERMISSION_DENIED")) return "DIAGNOSTIC_BILLING_REQUIRED";
  
  // Rate Limits
  if (upperMsg.includes("429")) return "DIAGNOSTIC_QUOTA_EXCEEDED";

  // High Demand / Capacity Issues (503 Service Unavailable)
  if (upperMsg.includes("503") || upperMsg.includes("HIGH DEMAND") || upperMsg.includes("OVERLOADED") || upperMsg.includes("CAPACITY")) return "DIAGNOSTIC_HIGH_TRAFFIC";
  
  // Server & Network
  if (upperMsg.includes("500") || upperMsg.includes("504") || upperMsg.includes("FETCH FAILED") || upperMsg.includes("NETWORK") || upperMsg.includes("FAILED TO FETCH")) return "DIAGNOSTIC_SERVER_ERROR";
  
  // Safety & Filters
  if (upperMsg.includes("SAFETY") || upperMsg.includes("BLOCKED") || upperMsg.includes("RECITATION") || upperMsg.includes("FINISH_REASON")) return "DIAGNOSTIC_SAFETY_BLOCK";
  
  // Data/Input Errors (Often 400 Bad Request due to invalid config)
  if (upperMsg.includes("400") || upperMsg.includes("BAD REQUEST") || upperMsg.includes("INVALID_ARGUMENT")) return "DIAGNOSTIC_INVALID_INPUT";

  // Output Errors
  if (upperMsg.includes("EMPTY_RESPONSE") || upperMsg.includes("NO IMAGE DATA")) return "DIAGNOSTIC_PROMPT_REJECTED";
  
  return msg;
};

/**
 * Enhanced Retry Logic with Exponential Backoff + Jitter
 * This prevents "thundering herd" issues where all retries hit the server at once.
 */
async function withRetry<T>(
  fn: () => Promise<T>, 
  retries = 3, 
  initialDelay = 2000,
  onStatusUpdate?: (msg: string) => void
): Promise<T> {
  try {
    return await fn();
  } catch (err: any) {
    if (err.message === "API_KEY_MISSING") throw err;

    const mapped = mapError(err);
    // Retry on Server Overload, High Traffic, Quota, or Connection issues
    const shouldRetry = mapped === "DIAGNOSTIC_QUOTA_EXCEEDED" || 
                        mapped === "DIAGNOSTIC_SERVER_ERROR" || 
                        mapped === "DIAGNOSTIC_HIGH_TRAFFIC";
    
    if (shouldRetry && retries > 0) {
      // Add Jitter: Random value between 0 and 1000ms
      const jitter = Math.random() * 1000;
      const totalDelay = initialDelay + jitter;
      
      const seconds = Math.ceil(totalDelay / 1000);
      
      // Update UI with status
      if (onStatusUpdate) {
        if (mapped === "DIAGNOSTIC_HIGH_TRAFFIC") {
           onStatusUpdate(`High traffic. Auto-retrying in ${seconds}s...`);
        } else {
           onStatusUpdate(`Connection interrupted. Retrying in ${seconds}s...`);
        }
      }
      
      await new Promise(res => setTimeout(res, totalDelay));
      
      // Increase delay exponentially for backoff
      return withRetry(fn, retries - 1, initialDelay * 2, onStatusUpdate);
    }
    throw new Error(mapped);
  }
}

export const refineBriefText = async (text: string): Promise<string> => {
  return withRetry(async () => {
    const ai = getAI();
    const response = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: `Refine this fashion brief for a professional director. Keep it concise (under 50 words). Original: "${text}"`,
    });
    return response.text?.trim() || text;
  }).catch(() => text);
};

export const generateCampaignConcepts = async (
  brief: CreativeBrief
): Promise<CampaignConcept[]> => {
  return withRetry(async () => {
    const ai = getAI();
    const activeOutfitsCount = brief.outfits.filter(o => o !== null).length;

    const systemInstruction = `Role: Commercial Art Director.
    Task: Create exactly 6 specific shot concepts for a product campaign based on the provided product and model references.
    The shots MUST be a mix of:
    1. Product Hero Shot (Product only)
    2. Model Application (Model interacting with product)
    3. Lifestyle Context (Model with product in environment)
    4. Hand Holding Product (Close up of hand and product)
    5. Environmental Product (Product only in environment)
    6. Macro Texture Swatch (Extreme close up of product texture/details)
    Output: Return a JSON array of exactly 6 objects. No additional text.`;

    const parts: any[] = [{ text: `Directive: ${brief.creativeDirection}\nEnvironment: ${brief.environment}` }];
    
    // Only send 1 identity anchor for theme generation to avoid context overload
    const identity = brief.anchorImages.find(img => img !== null);
    if (identity) {
      const mime = identity.split(';')[0].split(':')[1] || 'image/jpeg';
      parts.push({ text: `Identity Reference` });
      parts.push({ inlineData: { mimeType: mime, data: identity.split(',')[1] } });
    }

    if (brief.productImage) {
      const mime = brief.productImage.split(';')[0].split(':')[1] || 'image/jpeg';
      parts.push({ text: `Product Reference` });
      parts.push({ inlineData: { mimeType: mime, data: brief.productImage.split(',')[1] } });
    }
    
    brief.outfits.forEach((outfit, idx) => {
      if (outfit) {
        const mime = outfit.split(';')[0].split(':')[1] || 'image/jpeg';
        parts.push({ text: `Wardrobe Look ${idx + 1}` });
        parts.push({ inlineData: { mimeType: mime, data: outfit.split(',')[1] } });
      }
    });

    const generate = async (model: string) => {
      const response = await ai.models.generateContent({
        model,
        contents: { parts },
        config: {
          systemInstruction,
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                id: { type: Type.STRING },
                themeIndex: { type: Type.NUMBER },
                title: { type: Type.STRING },
                description: { type: Type.STRING },
                prompt: { type: Type.STRING },
                shotType: { type: Type.STRING, description: "Must be 'product_only' or 'model_and_product'" },
              },
              required: ["id", "themeIndex", "title", "description", "prompt", "shotType"],
            },
          },
        },
      });
      if (!response.text) throw new Error("Concept generation returned empty.");
      return extractJSON(response.text.trim());
    };

    // ATTEMPT 1: Try Pro Model (High Reasoning)
    try {
      return await generate('gemini-3-pro-preview');
    } catch (err: any) {
      const msg = mapError(err);
      // ATTEMPT 2: Fallback to Flash Model (High Availability) if Pro is overloaded
      if (msg === "DIAGNOSTIC_HIGH_TRAFFIC" || msg === "DIAGNOSTIC_SERVER_ERROR") {
        console.warn("Pro model overloaded, falling back to Flash for concepts...");
        return await generate('gemini-3-flash-preview');
      }
      throw err;
    }
  });
};

export const regenerateSingleThemeConcept = async (
  brief: CreativeBrief,
  themeIndex: number
): Promise<CampaignConcept> => {
  return withRetry(async () => {
    const ai = getAI();
    const outfit = brief.outfits[themeIndex - 1];
    
    const systemInstruction = `Role: Fashion Strategy Director. 
    Generate a NEW campaign concept for Theme #${themeIndex}. 
    Output: Return JSON object only.`;

    const parts: any[] = [{ text: `Base Direction: ${brief.creativeDirection}\nContext: ${brief.environment}` }];
    
    if (outfit) {
      const mime = outfit.split(';')[0].split(':')[1] || 'image/jpeg';
      parts.push({ text: `Wardrobe Reference:` });
      parts.push({ inlineData: { mimeType: mime, data: outfit.split(',')[1] } });
    }

    if (brief.productImage) {
      const mime = brief.productImage.split(';')[0].split(':')[1] || 'image/jpeg';
      parts.push({ text: `Product Reference:` });
      parts.push({ inlineData: { mimeType: mime, data: brief.productImage.split(',')[1] } });
    }

    const generate = async (model: string) => {
      const response = await ai.models.generateContent({
        model,
        contents: { parts },
        config: {
          systemInstruction,
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              id: { type: Type.STRING },
              themeIndex: { type: Type.NUMBER },
              title: { type: Type.STRING },
              description: { type: Type.STRING },
              prompt: { type: Type.STRING },
            },
            required: ["id", "themeIndex", "title", "description", "prompt"],
          },
        },
      });
      if (!response.text) throw new Error("Single concept regeneration failed.");
      return extractJSON(response.text.trim());
    };

    // ATTEMPT 1: Try Pro Model
    try {
      return await generate('gemini-3-pro-preview');
    } catch (err: any) {
      const msg = mapError(err);
      // ATTEMPT 2: Fallback to Flash Model if Pro is overloaded
      if (msg === "DIAGNOSTIC_HIGH_TRAFFIC" || msg === "DIAGNOSTIC_SERVER_ERROR") {
        return await generate('gemini-3-flash-preview');
      }
      throw err;
    }
  });
};

export const generateTwinImage = async (
  brief: CreativeBrief,
  outfitImage: string | null,
  conceptPrompt: string,
  variationSource?: string,
  strength: VariationStrength = 'medium',
  isPro: boolean = false,
  onStatusUpdate?: (msg: string) => void,
  shotType?: string
): Promise<string> => {
  // Internal function to attempt generation with a specific model and prompt robustness level
  const attemptGeneration = async (useProModel: boolean, isRetryPass = false): Promise<string> => {
    return withRetry(async () => {
      const ai = getAI();
      const model = useProModel ? 'gemini-3-pro-image-preview' : 'gemini-2.5-flash-image';
      
      const parts: any[] = [];
      // Payload Efficiency: Use all available anchor images to maximize likeness
      const identityImages = brief.anchorImages.filter(img => img !== null);
      
      if (shotType !== 'product_only' && identityImages.length > 0) {
        parts.push({ text: "IDENTITY REFERENCE IMAGES (The subject must look exactly like this person):" });
        identityImages.forEach(identity => {
          if (identity) {
            const mime = identity.split(';')[0].split(':')[1] || 'image/jpeg';
            parts.push({ inlineData: { mimeType: mime, data: identity.split(',')[1] } });
          }
        });
      }

      // If strictly a retry pass, skip the outfit to simplify the request and avoid payload issues
      if (shotType !== 'product_only' && outfitImage && !isRetryPass) {
        parts.push({ text: "WARDROBE REFERENCE:" });
        const mime = outfitImage.split(';')[0].split(':')[1] || 'image/jpeg';
        parts.push({ inlineData: { mimeType: mime, data: outfitImage.split(',')[1] } });
      }

      if (brief.productImage && !isRetryPass) {
        parts.push({ text: "PRODUCT REFERENCE:" });
        const mime = brief.productImage.split(';')[0].split(':')[1] || 'image/jpeg';
        parts.push({ inlineData: { mimeType: mime, data: brief.productImage.split(',')[1] } });
      }

      // SAFE MODE PROTOCOL:
      // If this is a retry pass (isRetryPass = true), we strip ALL technical physics and brands.
      // We only send the core concept. This prevents "Recitation" errors where the model 
      // refuses to generate "Sony" or "Vogue" style content due to copyright filters.
      
      let finalPrompt = "";
      
      let likenessPrompt = '';
      if (shotType !== 'product_only') {
        if (strength === 'high') {
          likenessPrompt = 'CRITICAL INSTRUCTION: The subject MUST be an EXACT clone of the person in the provided IDENTITY REFERENCE IMAGES. Maintain 100% exact facial features, bone structure, eye shape, nose shape, jawline, and overall identity. DO NOT alter their face in any way. The reference images dictate the exact person to be generated.';
        } else if (strength === 'medium') {
          likenessPrompt = 'CRITICAL INSTRUCTION: The subject MUST look exactly like the person in the provided IDENTITY REFERENCE IMAGES. Maintain exact facial features, bone structure, and overall likeness.';
        } else {
          likenessPrompt = 'INSTRUCTION: The subject should resemble the person in the provided IDENTITY REFERENCE IMAGES, maintaining their general vibe and features.';
        }
      } else {
        likenessPrompt = 'CRITICAL INSTRUCTION: Focus entirely on the product. Do not include any people or faces in this shot.';
      }
      
      if (isRetryPass) {
        // Simple, safe prompt for fallback
        finalPrompt = `Commercial photography: ${conceptPrompt}. ${brief.environment}. High quality, professional lighting. ${likenessPrompt}`;
      } else {
        // Detailed prompt for primary attempt
        const sensorDNA = PHOTOGRAPHY_PHYSICS[brief.sensor as keyof typeof PHOTOGRAPHY_PHYSICS] || PHOTOGRAPHY_PHYSICS['Sony A7R V'];
        const lightingDNA = LIGHTING_PHYSICS[brief.lightingTopology as keyof typeof LIGHTING_PHYSICS] || LIGHTING_PHYSICS['Studio Softbox'];
        
        finalPrompt = `
          Commercial editorial shot.
          Concept: ${conceptPrompt}.
          Location: ${brief.environment}.
          Camera Style: ${sensorDNA}
          Lighting Setup: ${lightingDNA}.
          ${likenessPrompt}
          Do not include: ${brief.negativePrompt}, text, watermark, low quality, distorted.
        `.trim();
      }

      parts.push({ text: finalPrompt });

      // STRICT CONFIG:
      // Flash model is very sensitive to imageConfig. 
      // If retrying (Safe Mode), we remove imageConfig entirely to use model defaults.
      // If Pro model, we use full config.
      
      let imageConfig: any = undefined;

      if (useProModel) {
        imageConfig = {
          aspectRatio: brief.aspectRatio,
          imageSize: brief.imageSize,
        };
      } else if (!isRetryPass) {
        // Only attempt aspect ratio on first pass for Flash
        imageConfig = {
           aspectRatio: brief.aspectRatio,
        };
      }

      const response = await ai.models.generateContent({
        model,
        contents: { parts },
        config: { 
          imageConfig,
          // Explicitly disable safety filters to prevent silent blocking
          safetySettings: [
            { category: HarmCategory.HARM_CATEGORY_HARASSMENT, threshold: HarmBlockThreshold.BLOCK_NONE },
            { category: HarmCategory.HARM_CATEGORY_HATE_SPEECH, threshold: HarmBlockThreshold.BLOCK_NONE },
            { category: HarmCategory.HARM_CATEGORY_SEXUALLY_EXPLICIT, threshold: HarmBlockThreshold.BLOCK_NONE },
            { category: HarmCategory.HARM_CATEGORY_DANGEROUS_CONTENT, threshold: HarmBlockThreshold.BLOCK_NONE },
          ]
        },
      });

      const candidate = response.candidates?.[0];
      
      // Handle Safety, Recitation, or other blockers by failing over to the Flash/Safe model
      if (!candidate || candidate.finishReason === "SAFETY" || candidate.finishReason === "RECITATION" || candidate.finishReason === "OTHER") {
        if (!isRetryPass) return attemptGeneration(false, true); // Fallback to Flash + Safe Prompt
        throw new Error("DIAGNOSTIC_SAFETY_BLOCK");
      }

      const imagePart = candidate.content.parts?.find(p => !!p.inlineData);
      if (imagePart?.inlineData) {
        return `data:image/png;base64,${imagePart.inlineData.data}`;
      }

      if (!isRetryPass) return attemptGeneration(false, true);
      throw new Error("EMPTY_RESPONSE");
    });
  };

  return attemptGeneration(isPro);
};